import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import QuizTaker from "./QuizTaker";
import LessonWorkspace from "./LessonWorkspace";
// ClassroomSidebar REMOVED - Zero-Box Protocol
import InvisibleTeacher from "./InvisibleTeacher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function UnifiedCourses({ onGradeLevelChange, user, isAdmin = false }) {
  // Grade and subject selection
  const [selectedGrade, setSelectedGrade] = useState(null); // Show grade selection by default
  const [selectedSubject, setSelectedSubject] = useState(null);
  
  // Curriculum data
  const [curriculum, setCurriculum] = useState(null);
  const [librarySubjects, setLibrarySubjects] = useState([]);
  const [courseProgress, setCourseProgress] = useState({});
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  
  // Lesson viewing
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [viewingLesson, setViewingLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);
  
  // Quiz state
  const [takingQuiz, setTakingQuiz] = useState(null);
  const quizContainerRef = useRef(null);
  
  // Error/status
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  
  // Sabbath curriculum lock (continuous weekly Sabbath — Genesis 2:2-3)
  // IMPORTANT: Biblical days run sundown to sundown
  const [isHighSabbath, setIsHighSabbath] = useState(false);
  const [isSabbath, setSabbath] = useState(false);
  const [isSabbathEnding, setSabbathEnding] = useState(false);
  const [biblicalDay, setBiblicalDay] = useState(null);
  const [isAfterSunset, setIsAfterSunset] = useState(false);
  const [curriculumLockChecked, setCurriculumLockChecked] = useState(false);
  
  // Check Sabbath status on mount
  const checkCurriculumStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      // Get user's timezone offset in hours (e.g., -5 for EST, +2 for Israel)
      const tzOffsetMinutes = new Date().getTimezoneOffset();
      const tzOffsetHours = -tzOffsetMinutes / 60; // Negate because JS returns opposite sign
      
      const response = await axios.get(`${API}/celestial/curriculum-status?tz_offset_hours=${tzOffsetHours}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsHighSabbath(response.data.is_high_sabbath);
      setSabbath(response.data.is_sabbath || false);
      setSabbathEnding(response.data.is_sabbath_ending || false);
      setBiblicalDay(response.data.biblical_day || null);
      setIsAfterSunset(response.data.is_after_sunset || false);
    } catch (err) {
      console.error("Failed to check curriculum status:", err);
    } finally {
      setCurriculumLockChecked(true);
    }
  }, []);
  
  useEffect(() => {
    checkCurriculumStatus();
  }, [checkCurriculumStatus]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 13 INDIVIDUAL GRADE TIERS (No Block Grouping)
  // ═══════════════════════════════════════════════════════════════════════════
  // PRIMARY: K, 1, 2, 3, 4, 5
  // MIDDLE: 6, 7, 8
  // HIGH SCHOOL: 9, 10, 11, 12
  // SEMINARY: Triggered after Grade 12 completion (separate from K-12)
  // ═══════════════════════════════════════════════════════════════════════════

  const gradeLevels = [
    // PRIMARY SCHOOL - THE RECOGNITION PHASE
    { id: "K", name: "Kindergarten", school: "PRIMARY", description: "Age 5 - Visual Recognition", icon: "🌱" },
    { id: "1", name: "Grade 1", school: "PRIMARY", description: "Age 6 - Calendar Basics", icon: "1️⃣" },
    { id: "2", name: "Grade 2", school: "PRIMARY", description: "Age 7 - Phenotype Recognition", icon: "2️⃣" },
    { id: "3", name: "Grade 3", school: "PRIMARY", description: "Age 8 - The 12 Gates", icon: "3️⃣" },
    { id: "4", name: "Grade 4", school: "PRIMARY", description: "Age 9 - The 4 Seasons", icon: "4️⃣" },
    { id: "5", name: "Grade 5", school: "PRIMARY", description: "Age 10 - Feasts of YHWH", icon: "5️⃣" },
    // MIDDLE SCHOOL - THE MECHANICAL PHASE
    { id: "6", name: "Grade 6", school: "MIDDLE", description: "Age 11 - 364-Day Math", icon: "6️⃣" },
    { id: "7", name: "Grade 7", school: "MIDDLE", description: "Age 12 - 91-Day Jubilee", icon: "7️⃣" },
    { id: "8", name: "Grade 8", school: "MIDDLE", description: "Age 13 - Observatory Intro", icon: "8️⃣" },
    // HIGH SCHOOL - THE LEGAL & DEFENSE PHASE
    { id: "9", name: "Grade 9", school: "HIGH", description: "Age 14 - Council of Nicaea", icon: "9️⃣" },
    { id: "10", name: "Grade 10", school: "HIGH", description: "Age 15 - Live Observatory", icon: "🔟" },
    { id: "11", name: "Grade 11", school: "HIGH", description: "Age 16 - Legal Sovereignty", icon: "⬆️" },
    { id: "12", name: "Grade 12", school: "HIGH", description: "Age 17 - Senior Practicum", icon: "🎓" },
  ];

  // YKBN DOCTRINE CONTENT per grade level
  const subjectsByGrade = {
    // K-2: PHENOTYPE RECOGNITION (Bronze & Wooly Hair vs. Roman Idol)
    "K": [
      { id: "phenotype-recognition", name: "Phenotype Recognition", icon: "👤", description: "The True Brother vs. The Roman Idol" },
      { id: "visual-identity", name: "Visual Identity", icon: "👁️", description: "Bronze Skin & Wooly Hair" },
      { id: "hebrew-alphabet", name: "Hebrew Alphabet", icon: "א", description: "Learning Hebrew Letters" },
      { id: "4-portals-intro", name: "The 4 Portals", icon: "🚪", description: "Introduction to Seasonal Gates" },
    ],
    "1": [
      { id: "phenotype-recognition", name: "Phenotype Recognition", icon: "👤", description: "Afro-Shemitic Features" },
      { id: "sabbath-days", name: "Weekly Sabbath", icon: "🕯️", description: "Genesis 2:2-3 · Continuous 7th-day rest" },
      { id: "sun-observation", name: "Sun Observation", icon: "☀️", description: "Sunrise & Sunset" },
      { id: "hebrew-reading", name: "Hebrew Reading", icon: "📖", description: "Basic Hebrew Words" },
    ],
    "2": [
      { id: "phenotype-recognition", name: "Phenotype Recognition", icon: "👤", description: "Rev 1:14-15 Study" },
      { id: "serapis-idol", name: "The Roman Idol", icon: "🏛️", description: "Identifying the Serapis Image" },
      { id: "6-gates", name: "The 6 Gates", icon: "🚪", description: "Gates of the Luminaries" },
      { id: "moon-parts", name: "Moon Parts", icon: "🌙", description: "14 Parts of Light" },
    ],
    // GRADES 3-5: THE 12 GATES & THE 4 SEASONS
    "3": [
      { id: "12-gates", name: "The 12 Gates", icon: "🚪", description: "Complete Gate System" },
      { id: "4-seasons", name: "The 4 Seasons", icon: "🍂", description: "Seasonal Portals" },
      { id: "phenotype-test", name: "Phenotype Test", icon: "✅", description: "Visual Literacy Exam" },
      { id: "hebrew-writing", name: "Hebrew Writing", icon: "✍️", description: "Writing Hebrew Script" },
    ],
    "4": [
      { id: "12-months", name: "The 12 Months", icon: "📅", description: "Hebrew Month Names" },
      { id: "4-intercalary", name: "The 4 Intercalary Days", icon: "📍", description: "Days 91, 182, 273, 364" },
      { id: "seasonal-portals", name: "Seasonal Portals", icon: "🌅", description: "Equinox & Solstice" },
      { id: "basic-math", name: "Calendar Math", icon: "🔢", description: "30-Day Month Structure" },
    ],
    "5": [
      { id: "feasts-yhwh", name: "Feasts of YHWH", icon: "🎉", description: "Leviticus 23 Study" },
      { id: "passover-calc", name: "Passover Calculation", icon: "🍞", description: "Finding Abib 14" },
      { id: "gate-seasons", name: "Gate & Seasons Review", icon: "📚", description: "Primary Graduation Prep" },
      { id: "primary-exam", name: "Primary Completion", icon: "🎓", description: "Recognition Phase Final" },
    ],
    // GRADES 6-8: TECHNICAL 364-DAY MATH & 91ST-DAY JUBILEE
    "6": [
      { id: "364-day-math", name: "364-Day Math", icon: "🔢", description: "12×30 + 4 = 364" },
      { id: "quarterly-structure", name: "Quarterly Structure", icon: "📊", description: "91-Day Quarters" },
      { id: "enoch-intro", name: "Book of Enoch Intro", icon: "📜", description: "Chapters 72-82" },
      { id: "luminary-laws", name: "Luminary Laws", icon: "☀️🌙", description: "Sun & Moon Mechanics" },
    ],
    "7": [
      { id: "91-day-jubilee", name: "91-Day Jubilee", icon: "🎺", description: "Quarterly Reset Protocol" },
      { id: "intercalary-gates", name: "Intercalary Gates", icon: "🚪", description: "Jubilee Gate Days" },
      { id: "enoch-technical", name: "Enoch Technical", icon: "📐", description: "Luminary Calculations" },
      { id: "national-gates", name: "National Jubilee Gates", icon: "🏛️", description: "M1, M4, M7, M10" },
    ],
    "8": [
      { id: "astronomy-basics", name: "Astronomy Basics", icon: "🔭", description: "RA/Dec Coordinates" },
      { id: "watchman-api-intro", name: "Watchman API Intro", icon: "💻", description: "Observatory Basics" },
      { id: "portal-calculation", name: "Portal Calculation", icon: "📊", description: "Gate Entry Dates" },
      { id: "middle-exam", name: "Middle Completion", icon: "🎓", description: "Mechanical Phase Final" },
    ],
    // GRADES 9-12: COUNCIL OF NICAEA, LEGAL SOVEREIGNTY, LIVE OBSERVATORY
    "9": [
      { id: "council-nicaea", name: "Council of Nicaea", icon: "⚔️", description: "The Hijack (325 AD)" },
      { id: "replacement-theology", name: "Replacement Theology", icon: "📜", description: "The Transformation" },
      { id: "hebrew-sonship", name: "Hebrew Sonship", icon: "👥", description: "Ben vs. Huios" },
      { id: "transformation-matrix", name: "Transformation Matrix", icon: "🔄", description: "Yahshua to Jesus" },
    ],
    "10": [
      { id: "13th-month-refutation", name: "13th Month Refutation", icon: "❌", description: "Rejecting Adar II" },
      { id: "live-observatory", name: "Live Observatory", icon: "🔭", description: "Real-Time Tracking" },
      { id: "gate-tracking", name: "Gate Tracking", icon: "📡", description: "Portal Detection" },
      { id: "observatory-practical", name: "Observatory Practical", icon: "✅", description: "Live Data Test" },
    ],
    "11": [
      { id: "ykbn-doctrine", name: "The Ykbn Doctrine", icon: "📜", description: "Legal Standing" },
      { id: "national-sovereignty", name: "National Sovereignty", icon: "🏛️", description: "Self-Determination" },
      { id: "firstborn-contract", name: "Firstborn Contract", icon: "📝", description: "Exodus 4:22" },
      { id: "doctrine-defense", name: "Doctrine Defense", icon: "🛡️", description: "Apologetics" },
    ],
    "12": [
      { id: "senior-practicum", name: "Senior Practicum", icon: "🎓", description: "Live Watchman API" },
      { id: "passover-verification", name: "Passover Verification", icon: "🍞", description: "2026 Verification" },
      { id: "calendar-governance", name: "Calendar Governance", icon: "⚖️", description: "Community Leadership" },
      { id: "seminary-prep", name: "Seminary Preparation", icon: "🏛️", description: "Admissions Module" },
    ],
  };

  useEffect(() => {
    fetchLibrary();
  }, []);

  useEffect(() => {
    if (selectedGrade) {
      fetchProgressForGrade(selectedGrade);
    }
  }, [selectedGrade, librarySubjects]);

  const fetchLibrary = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibrarySubjects(response.data.subjects || []);
    } catch (err) {
      console.error("Error fetching library:", err);
    }
  };

  const fetchProgressForGrade = async (grade) => {
    setLoadingProgress(true);
    const subjects = subjectsByGrade[grade] || [];
    const progressMap = {};

    try {
      const token = localStorage.getItem("token");
      for (const subject of subjects) {
        try {
          const response = await axios.get(
            `${API}/lesson/progress/${subject.id}/${grade}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          progressMap[subject.id] = response.data;
        } catch {
          progressMap[subject.id] = { 
            total_lessons: 0, 
            lessons_completed_count: 0,
            completion_percentage: 0,
            can_take_exam: false
          };
        }
      }
      setCourseProgress(progressMap);
    } catch (err) {
      console.error("Error fetching progress:", err);
    } finally {
      setLoadingProgress(false);
    }
  };

  const hasCurriculum = (subjectId) => {
    return librarySubjects.some(s => s.subject_id === subjectId);
  };

  const handleSubjectClick = async (subject) => {
    setSelectedSubject(subject);
    setError("");
    setStatusMessage("");
    
    // ALWAYS try to load first - curriculum might already exist
    await loadCurriculumWithFallback(subject.id);
  };

  // ── Dashboard "Resume Lesson" deep-link consumer (Courses section)
  // 2026-02: when the student taps Continue on the dashboard, we
  // receive a `ylc:resume-target` window event (or read the storage
  // hint on mount) and auto-open the saved grade / subject / lesson.
  const consumeResumeTarget = useCallback((overrideTarget) => {
    let target = overrideTarget;
    if (!target) {
      try {
        target = JSON.parse(localStorage.getItem("ylc_resume_target") || "null");
      } catch {
        target = null;
      }
    }
    if (!target) return;
    // Only consume targets that explicitly belong to the Courses section.
    if (target.section !== "courses") return;
    try { localStorage.removeItem("ylc_resume_target"); } catch {}

    if (target.gradeLevel) {
      setSelectedGrade(target.gradeLevel);
    }

    const subjectsList = librarySubjects.length ? librarySubjects : null;

    const openLesson = (subj) => {
      setSelectedSubject(subj);
      loadCurriculumWithFallback(subj.id).then(() => {
        if (target.lessonNumber != null && target.lessonTitle) {
          setViewingLesson({
            lesson_number: target.lessonNumber,
            title: target.lessonTitle,
          });
        }
      });
    };

    if (subjectsList) {
      let subj = null;
      if (target.subjectId) {
        subj = subjectsList.find((s) => s.subject_id === target.subjectId || s.id === target.subjectId);
      }
      if (!subj && target.subject) {
        const want = String(target.subject).toLowerCase();
        subj = subjectsList.find(
          (s) =>
            (s.subject_name && s.subject_name.toLowerCase().includes(want)) ||
            (s.name && s.name.toLowerCase().includes(want))
        );
      }
      if (subj) {
        // Normalize shape so handleSubjectClick / state expects {id, name}
        openLesson({
          id: subj.subject_id || subj.id,
          name: subj.subject_name || subj.name,
          icon: subj.icon,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [librarySubjects]);

  useEffect(() => {
    consumeResumeTarget();
  }, [consumeResumeTarget]);

  useEffect(() => {
    const handler = (e) => consumeResumeTarget(e?.detail);
    window.addEventListener("ylc:resume-target", handler);
    return () => window.removeEventListener("ylc:resume-target", handler);
  }, [consumeResumeTarget]);

  const loadCurriculumWithFallback = async (subjectId, retryCount = 0) => {
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to access curriculum");
        setLoading(false);
        return;
      }
      
      // Try to load existing curriculum first - pass grade for YKBN content
      const response = await axios.get(`${API}/library/${subjectId}?grade=${selectedGrade}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      
      if (response.data.success && response.data.curriculum) {
        const curriculumData = response.data.curriculum;
        
        // For YKBN doctrine content, use the curriculum's grade
        const targetGrade = curriculumData.grade || selectedGrade;
        
        // Find ANY grade level with lessons
        const gradeKeys = Object.keys(curriculumData.grade_levels || {});
        let foundGrade = targetGrade;
        let foundLessons = curriculumData.grade_levels?.[targetGrade]?.lessons;
        
        if (!foundLessons?.length) {
          // Try to find any grade with lessons
          for (const grade of gradeKeys) {
            const lessons = curriculumData.grade_levels[grade]?.lessons;
            if (lessons?.length > 0) {
              foundGrade = grade;
              foundLessons = lessons;
              break;
            }
          }
        }
        
        if (foundLessons?.length > 0) {
          // SUCCESS - We have lessons!
          setCurriculum(curriculumData);
          fetchCompletedLessons(subjectId, foundGrade);
          setLoading(false);
          setGenerating(false);
          return;
        }
      }
      
      // No lessons found - try generating
      if (retryCount < 2) {
        await generateAndWait(subjectId, retryCount);
      } else {
        setError("Unable to load curriculum. Please try again later.");
        setLoading(false);
      }
      
    } catch (err) {
      console.error("Load error:", err);
      
      if (err.response?.status === 404 || err.code === 'ECONNABORTED') {
        // Curriculum doesn't exist or timeout - generate it
        if (retryCount < 2) {
          await generateAndWait(subjectId, retryCount);
        } else {
          setError("Curriculum generation failed. Please refresh and try again.");
          setLoading(false);
        }
      } else if (err.response?.status === 402) {
        alert("Subscription required. Please renew to access curriculum.");
        setLoading(false);
      } else {
        setError("Connection error. Please check your internet and try again.");
        setLoading(false);
      }
    }
  };

  const generateAndWait = async (subjectId, retryCount) => {
    setGenerating(true);
    setStatusMessage("Generating curriculum... This takes 1-2 minutes.");
    
    try {
      const token = localStorage.getItem("token");
      
      // Trigger generation
      await axios.post(
        `${API}/library/generate/${subjectId}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // Short timeout - generation is async
        }
      ).catch(() => {
        // Generation might already be in progress - that's OK
      });
      
      // Quick poll - check every 3 seconds for up to 90 seconds
      for (let i = 0; i < 30; i++) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        try {
          const checkResponse = await axios.get(`${API}/library/${subjectId}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000
          });
          
          if (checkResponse.data.success) {
            const currData = checkResponse.data.curriculum;
            const gradeKeys = Object.keys(currData?.grade_levels || {});
            
            // Check ALL grades for lessons
            for (const grade of gradeKeys) {
              if (currData.grade_levels[grade]?.lessons?.length > 0) {
                setCurriculum(currData);
                setSelectedGrade(grade);
                fetchCompletedLessons(subjectId, grade);
                setStatusMessage("");
                setGenerating(false);
                setLoading(false);
                fetchLibrary();
                return;
              }
            }
          }
        } catch {
          // Still generating - continue polling
        }
        
        // Update status message
        if (i === 10) setStatusMessage("Still generating... almost there!");
        if (i === 20) setStatusMessage("Generation taking longer than expected...");
      }
      
      // Timeout - try one more reload
      setStatusMessage("Finalizing...");
      await loadCurriculumWithFallback(subjectId, retryCount + 1);
      
    } catch (err) {
      console.error("Generation error:", err);
      setError("Generation failed. Please try again.");
      setGenerating(false);
      setLoading(false);
    }
  };

  const loadCurriculum = async (subjectId) => {
    // Redirect to new robust method
    await loadCurriculumWithFallback(subjectId);
  };

  // Legacy function - redirects to new method
  const generateCurriculum = async (subjectId) => {
    await generateAndWait(subjectId, 0);
  };

  const fetchCompletedLessons = async (subjectId, grade) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/lesson/completed/${subjectId}/${grade}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompletedLessons(response.data.completed_lessons || []);
    } catch {
      setCompletedLessons([]);
    }
  };

  const markLessonComplete = async (lessonNumber) => {
    if (!selectedSubject || !selectedGrade) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/lesson/complete`,
        {
          subject_id: selectedSubject.id,
          grade_level: selectedGrade,
          lesson_number: lessonNumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!completedLessons.includes(lessonNumber)) {
        setCompletedLessons([...completedLessons, lessonNumber]);
      }
    } catch (err) {
      console.error("Error marking lesson complete:", err);
    }
  };

  const fetchLessonContent = async (lessonNumber) => {
    if (!selectedSubject) return;
    
    setLoadingContent(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/lesson/content/${selectedSubject.id}/${selectedGrade}/${lessonNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLessonContent(response.data);
      markLessonComplete(lessonNumber);
    } catch (err) {
      console.error("Error fetching lesson:", err);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleLessonClick = (index, lessonNumber) => {
    if (expandedLesson === index) {
      setExpandedLesson(null);
      setLessonContent(null);
    } else {
      setExpandedLesson(index);
      fetchLessonContent(lessonNumber);
    }
  };

  const handleBackToSubjects = () => {
    setSelectedSubject(null);
    setCurriculum(null);
    setExpandedLesson(null);
    setLessonContent(null);
    setError("");
    setStatusMessage("");
  };

  const handleBackToGrades = () => {
    setSelectedGrade("");
    setSelectedSubject(null);
    setCurriculum(null);
    setCourseProgress({});
  };

  // Render progress indicator
  const renderProgress = (subjectId) => {
    const progress = courseProgress[subjectId];
    const hasContent = hasCurriculum(subjectId);
    
    if (!hasContent) {
      return <span style={{ color: "#888", fontSize: "0.8rem" }}>Tap to generate</span>;
    }
    
    if (!progress || progress.total_lessons === 0) {
      return <span style={{ color: "#87CEEB", fontSize: "0.8rem" }}>✓ Ready</span>;
    }
    
    const pct = progress.completion_percentage || 0;
    return (
      <div style={{ width: "100%" }}>
        <div style={{ 
          height: "4px", 
          background: "#333", 
          borderRadius: "2px",
          marginBottom: "4px"
        }}>
          <div style={{ 
            width: `${pct}%`, 
            height: "100%", 
            background: pct === 100 ? "#90EE90" : "#FFD700",
            borderRadius: "2px"
          }} />
        </div>
        <span style={{ color: pct === 100 ? "#90EE90" : "#888", fontSize: "0.75rem" }}>
          {pct === 100 ? "✓ Complete" : `${progress.lessons_completed_count}/${progress.total_lessons} lessons`}
        </span>
      </div>
    );
  };

  // If viewing a lesson in workspace
  if (viewingLesson) {
    const targetGradeForList = curriculum?.grade || selectedGrade;
    const currentLessonsList = curriculum?.grade_levels?.[targetGradeForList]?.lessons || [];
    return (
      <LessonWorkspace
        subjectId={selectedSubject?.id}
        subjectName={selectedSubject?.name}
        subjectIcon={selectedSubject?.icon}
        gradeLevel={selectedGrade}
        lessonNumber={viewingLesson.lesson_number}
        lessonTitle={viewingLesson.title}
        sectionContext="courses"
        onBack={() => setViewingLesson(null)}
        onTakeQuiz={(lessonNum) => setTakingQuiz({ lessonNumber: lessonNum })}
        onContentLoaded={(content) => setLessonContent(content)}
        user={user}
        lessonsList={currentLessonsList}
        onNextLesson={(lesson) => {
          const nextNum = lesson.lesson_number ?? lesson.number;
          setViewingLesson({ ...lesson, lesson_number: nextNum });
          try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) {}
        }}
        onPrevLesson={(lesson) => {
          const prevNum = lesson.lesson_number ?? lesson.number;
          setViewingLesson({ ...lesson, lesson_number: prevNum });
          try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) {}
        }}
      />
      // InvisibleTeacher is already included in LessonWorkspace - no duplicate needed
    );
  }

  // If taking a quiz
  if (takingQuiz) {
    return (
      <>
        <div ref={quizContainerRef}>
          <QuizTaker
            subjectId={selectedSubject?.id}
            gradeLevel={selectedGrade}
            lessonNumber={takingQuiz.lessonNumber}
            onComplete={() => {
              setTakingQuiz(null);
              fetchProgressForGrade(selectedGrade);
            }}
            onCancel={() => {
              setTakingQuiz(null);
            }}
          />
        </div>
        
        {/* ZERO-BOX PROTOCOL: ClassroomSidebar REMOVED - No chat bubbles in Quiz mode */}
      </>
    );
  }

  // STEP 1: Grade Selection - 13 INDIVIDUAL GRADE TIERS (No Block Grouping)
  if (!selectedGrade) {
    // Group grades by school level for display organization
    const primaryGrades = gradeLevels.filter(g => g.school === "PRIMARY");
    const middleGrades = gradeLevels.filter(g => g.school === "MIDDLE");
    const highGrades = gradeLevels.filter(g => g.school === "HIGH");
    
    return (
      <div className="card" data-testid="grade-selection">
        <h2 style={{ color: "#FFD700", marginBottom: "10px" }}>📚 Select Your Grade</h2>
        <p style={{ color: "#aaa", marginBottom: "20px" }}>
          13 Individual Grade Levels - Select your enrollment path
        </p>
        
        {/* Scrollable vertical list of 13 individual grades */}
        <div style={{ 
          maxHeight: "55vh", 
          overflowY: "auto", 
          paddingRight: "10px",
          scrollbarWidth: "thin",
          scrollbarColor: "#FFD700 #111"
        }}>
          
          {/* PRIMARY SCHOOL - K through 5 */}
          <div style={{ marginBottom: "15px" }}>
            <h3 style={{ 
              color: "#90EE90", 
              fontSize: "0.85rem", 
              marginBottom: "8px",
              paddingBottom: "5px",
              borderBottom: "1px solid #333"
            }}>
              PRIMARY SCHOOL - Recognition Phase
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {primaryGrades.map(grade => (
                <button
                  key={grade.id}
                  data-testid={`grade-${grade.id}`}
                  onClick={() => {
                    setSelectedGrade(grade.id);
                    if (onGradeLevelChange) onGradeLevelChange(grade.id);
                  }}
                  style={{
                    background: "#1a1a1a",
                    border: "1px solid #90EE90",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                  }}
                >
                  <span style={{ fontSize: "1.2rem" }}>{grade.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#90EE90", fontSize: "0.9rem", fontWeight: "bold" }}>
                      {grade.name}
                    </div>
                    <div style={{ color: "#888", fontSize: "0.7rem" }}>
                      {grade.description}
                    </div>
                  </div>
                  <span style={{ color: "#555", fontSize: "0.8rem" }}>→</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* MIDDLE SCHOOL - 6 through 8 */}
          <div style={{ marginBottom: "15px" }}>
            <h3 style={{ 
              color: "#87CEEB", 
              fontSize: "0.85rem", 
              marginBottom: "8px",
              paddingBottom: "5px",
              borderBottom: "1px solid #333"
            }}>
              MIDDLE SCHOOL - Mechanical Phase
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {middleGrades.map(grade => (
                <button
                  key={grade.id}
                  data-testid={`grade-${grade.id}`}
                  onClick={() => {
                    setSelectedGrade(grade.id);
                    if (onGradeLevelChange) onGradeLevelChange(grade.id);
                  }}
                  style={{
                    background: "#1a1a1a",
                    border: "1px solid #87CEEB",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                  }}
                >
                  <span style={{ fontSize: "1.2rem" }}>{grade.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#87CEEB", fontSize: "0.9rem", fontWeight: "bold" }}>
                      {grade.name}
                    </div>
                    <div style={{ color: "#888", fontSize: "0.7rem" }}>
                      {grade.description}
                    </div>
                  </div>
                  <span style={{ color: "#555", fontSize: "0.8rem" }}>→</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* HIGH SCHOOL - 9 through 12 */}
          <div style={{ marginBottom: "15px" }}>
            <h3 style={{ 
              color: "#FFD700", 
              fontSize: "0.85rem", 
              marginBottom: "8px",
              paddingBottom: "5px",
              borderBottom: "1px solid #333"
            }}>
              HIGH SCHOOL - Legal & Defense Phase
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {highGrades.map(grade => (
                <button
                  key={grade.id}
                  data-testid={`grade-${grade.id}`}
                  onClick={() => {
                    setSelectedGrade(grade.id);
                    if (onGradeLevelChange) onGradeLevelChange(grade.id);
                  }}
                  style={{
                    background: "#1a1a1a",
                    border: "1px solid #FFD700",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                  }}
                >
                  <span style={{ fontSize: "1.2rem" }}>{grade.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#FFD700", fontSize: "0.9rem", fontWeight: "bold" }}>
                      {grade.name}
                    </div>
                    <div style={{ color: "#888", fontSize: "0.7rem" }}>
                      {grade.description}
                    </div>
                  </div>
                  <span style={{ color: "#555", fontSize: "0.8rem" }}>→</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* SEMINARY INFO - Separate from K-12 */}
          <div style={{ 
            marginTop: "10px",
            padding: "10px",
            background: "#0a0a0a",
            borderRadius: "6px",
            border: "1px solid #444"
          }}>
            <h3 style={{ color: "#DDA0DD", fontSize: "0.8rem", marginBottom: "5px" }}>
              🏛️ YKBN SEMINARY (After Grade 12)
            </h3>
            <p style={{ color: "#888", fontSize: "0.7rem", margin: 0 }}>
              Complete Grade 12 to unlock: Associate (2yr), Bachelor (4yr), Master (6yr), Doctorate (8yr+)
            </p>
          </div>
        </div>
      </div>
    );
  }

  // STEP 2: Subject Selection
  if (!selectedSubject) {
    // ── iter 70 grade-scoped view for everyone, incl. admin ────
    // 2026-02-21 user report: "For Grades 3 through 12, the content
    // is currently looping back to the Kindergarten level; please
    // ensure each grade level displays its specific lesson modules."
    // Root cause: the previous admin-bypass rendered the UNION of
    // subjects across all grades on every grade tile, so tapping
    // (e.g.) `hebrew-alphabet` on Grade 9 hit the grade-K
    // curriculum. Now every tile — student or admin — renders ONLY
    // the subjects assigned to that grade, so no cross-grade
    // leakage is possible. The admin can still reach every grade
    // (K through 12) via the Change-Grade button and the K-12
    // Academic / Special Edition entry cards on the dashboard.
    const subjects = subjectsByGrade[selectedGrade] || [];
    return (
      <div>
        <div className="card" data-testid="subject-selection">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
            <div>
              <h2 style={{ color: "#FFD700", margin: 0 }}>
                {gradeLevels.find(g => g.id === selectedGrade)?.name}
              </h2>
              <p style={{ color: "#aaa", margin: "5px 0 0 0", fontSize: "0.9rem" }}>
                Tap a subject to start learning
              </p>
            </div>
            <button
              onClick={handleBackToGrades}
              style={{ 
                width: "auto", 
                padding: "8px 15px",
                background: "#333",
                marginTop: 0
              }}
            >
              ← Change Grade
            </button>
          </div>

          {loadingProgress && (
            <p style={{ color: "#888", fontSize: "0.9rem" }}>Loading progress...</p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {subjects.map((subject, index) => (
              <button
                key={subject.id}
                data-testid={`subject-${subject.id}`}
                onClick={() => handleSubjectClick(subject)}
                style={{
                  background: "#1a1a2e",
                  border: "1px solid #333",
                  borderLeft: `4px solid ${hasCurriculum(subject.id) ? "#90EE90" : "#FFD700"}`,
                  borderRadius: "8px",
                  padding: "15px",
                  cursor: "pointer",
                  textAlign: "left",
                  width: "100%",
                  transition: "all 0.2s"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
                      <span style={{ fontSize: "1.5rem" }}>{subject.icon}</span>
                      <span style={{ color: "#fff", fontSize: "1rem", fontWeight: "600" }}>{subject.name}</span>
                    </div>
                    <p style={{ color: "#888", fontSize: "0.85rem", margin: "0 0 8px 0" }}>
                      {subject.description}
                    </p>
                    {renderProgress(subject.id)}
                  </div>
                  <span style={{ color: "#FFD700", fontSize: "1.5rem", marginLeft: "10px" }}>→</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // STEP 3: Curriculum View
  // For YKBN doctrine curriculum, the grade is stored directly in curriculum.grade
  const targetGrade = curriculum?.grade || selectedGrade;
  const gradeContent = curriculum?.grade_levels?.[targetGrade];
  const lessons = gradeContent?.lessons || [];

  return (
    <div>
      <div className="card" data-testid="curriculum-view">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <div>
            <h2 style={{ color: "#FFD700", margin: 0 }}>
              {selectedSubject.icon} {selectedSubject.name}
            </h2>
            <p style={{ color: "#aaa", margin: "5px 0 0 0", fontSize: "0.9rem" }}>
              {selectedGrade} • {lessons.length} Lessons
            </p>
          </div>
          <button
            onClick={handleBackToSubjects}
            style={{ 
              width: "auto", 
              padding: "8px 15px",
              background: "#333",
              marginTop: 0
            }}
          >
            ← Back
          </button>
        </div>

        {error && (
          <div style={{ 
            background: "#4d1a1a", 
            border: "1px solid #ff6b6b", 
            padding: "10px", 
            borderRadius: "6px",
            marginBottom: "15px",
            color: "#ff6b6b"
          }}>
            {error}
          </div>
        )}

        {statusMessage && (
          <div style={{ 
            background: "#1a4d1a", 
            border: "1px solid #90EE90", 
            padding: "10px", 
            borderRadius: "6px",
            marginBottom: "15px",
            color: "#90EE90"
          }}>
            {statusMessage}
          </div>
        )}

        {(loading || generating) && (
          <div style={{ textAlign: "center", padding: "40px", color: "#FFD700" }}>
            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>⏳</div>
            <p>{generating ? "Generating curriculum..." : "Loading..."}</p>
            {generating && (
              <p style={{ color: "#888", fontSize: "0.85rem", marginTop: "10px" }}>
                This may take 1-2 minutes for new curriculum
              </p>
            )}
          </div>
        )}

        {/* Show retry button if no lessons and not loading */}
        {!loading && !generating && lessons.length === 0 && selectedSubject && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div style={{ color: "#888", marginBottom: "20px" }}>
              No lessons found for this subject. Click below to load curriculum.
            </div>
            <button
              onClick={() => loadCurriculumWithFallback(selectedSubject.id)}
              style={{
                background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                color: "#000",
                border: "none",
                borderRadius: "8px",
                padding: "15px 30px",
                fontSize: "1rem",
                fontWeight: "bold",
                cursor: "pointer"
              }}
            >
              📚 Load Curriculum
            </button>
          </div>
        )}

        {!loading && !generating && lessons.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* Sabbath Rest Banner (appears on days 7, 14, 21, 28) */}
            {isSabbath && (
              <div style={{
                background: isHighSabbath 
                  ? "linear-gradient(135deg, #8B4513 0%, #CD853F 100%)"
                  : "linear-gradient(135deg, #2a1a3a 0%, #1a1a3a 100%)",
                borderRadius: "10px",
                padding: "15px",
                marginBottom: "10px",
                textAlign: "center",
                border: isHighSabbath ? "2px solid #FFD700" : "1px solid #4169E1"
              }}>
                <div style={{ color: "#fff", fontWeight: "bold", fontSize: "1rem", marginBottom: "5px" }}>
                  {isHighSabbath ? "🕊️ High Sabbath - The 15th Day" : `🕊️ Sabbath Rest - Day ${biblicalDay}`}
                </div>
                <div style={{ color: "#eee", fontSize: "0.85rem" }}>
                  {isHighSabbath 
                    ? "Traditional curriculum is available after sunset. Rest and rejoice!"
                    : "Sabbath is in effect until sundown. New lessons unlock after sunset."}
                </div>
                <div style={{ color: "#CD853F", fontSize: "0.75rem", marginTop: "8px" }}>
                  "Remember the Sabbath day, to keep it Set-Apart" - Exodus 20:8
                </div>
                <div style={{ color: "#87CEEB", fontSize: "0.7rem", marginTop: "5px" }}>
                  (Biblical days run from sundown to sundown)
                </div>
              </div>
            )}

            {/* Sabbath Ending Banner - lessons just unlocked! */}
            {isSabbathEnding && !isSabbath && (
              <div style={{
                background: "linear-gradient(135deg, #1a3a1a 0%, #0a2a0a 100%)",
                borderRadius: "10px",
                padding: "15px",
                marginBottom: "10px",
                textAlign: "center",
                border: "2px solid #90EE90"
              }}>
                <div style={{ color: "#90EE90", fontWeight: "bold", fontSize: "1rem", marginBottom: "5px" }}>
                  🌅 Sabbath Has Ended - Shavua Tov!
                </div>
                <div style={{ color: "#eee", fontSize: "0.85rem" }}>
                  The sun has set. All lessons are now unlocked. Have a blessed week!
                </div>
              </div>
            )}
            
            {lessons.map((lesson, index) => {
              const lessonNum = lesson.lesson_number || index + 1;
              const isCompleted = completedLessons.includes(lessonNum);
              const isExpanded = expandedLesson === index;
              
              // Curriculum lock logic:
              // - High Sabbath (15th of 1st/7th month): Lock ALL lessons
              // - Regular Sabbath (7, 14, 21, 28): Lock only NEW lessons (not yet completed)
              // - Completed lessons can be reviewed anytime
              const isLocked = isHighSabbath || (isSabbath && !isCompleted);
              
              return (
                <div key={index}>
                  <button
                    data-testid={`lesson-${lessonNum}`}
                    onClick={() => !isLocked && handleLessonClick(index, lessonNum)}
                    style={{
                      background: isExpanded ? "#2a2a4a" : isLocked ? "#1a1a1a" : "#222",
                      border: `1px solid ${isCompleted ? "#90EE90" : isLocked ? "#666" : "#333"}`,
                      borderRadius: "8px",
                      padding: "12px 15px",
                      cursor: isLocked ? "not-allowed" : "pointer",
                      textAlign: "left",
                      width: "100%",
                      opacity: isLocked ? 0.7 : 1
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ 
                          background: isCompleted ? "#90EE90" : isLocked ? "#666" : "#FFD700",
                          color: "#000",
                          width: "28px",
                          height: "28px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.85rem",
                          fontWeight: "bold"
                        }}>
                          {isCompleted ? "✓" : isLocked ? "🔒" : lessonNum}
                        </span>
                        <span style={{ color: isLocked ? "#888" : "#fff" }}>{lesson.title}</span>
                        {isLocked && (
                          <span style={{
                            background: isHighSabbath ? "#8B4513" : "#4a3a2a",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "0.7rem"
                          }}>
                            {isHighSabbath ? "Available after High Sabbath" : "Sabbath Rest - Review only"}
                          </span>
                        )}
                      </div>
                      <span style={{ color: "#888" }}>{isExpanded ? "▼" : "▶"}</span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div style={{ 
                      background: "#1a1a2e", 
                      padding: "15px", 
                      marginTop: "-1px",
                      borderRadius: "0 0 8px 8px",
                      border: "1px solid #333",
                      borderTop: "none"
                    }}>
                      {loadingContent ? (
                        <p style={{ color: "#888" }}>Loading lesson content...</p>
                      ) : lessonContent ? (
                        <div>
                          <p style={{ color: "#ccc", marginBottom: "15px", lineHeight: "1.6" }}>
                            {lessonContent.content?.introduction?.substring(0, 300) || "Lesson content loaded. Click to view full lesson."}...
                          </p>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <button
                              onClick={() => setViewingLesson({ ...lesson, lesson_number: lessonNum })}
                              style={{ 
                                width: "auto", 
                                padding: "8px 15px",
                                background: "#1a4d1a",
                                marginTop: 0
                              }}
                            >
                              📖 Open Full Lesson
                            </button>
                            <button
                              onClick={() => setTakingQuiz({ lessonNumber: lessonNum })}
                              style={{ 
                                width: "auto", 
                                padding: "8px 15px",
                                background: "#4d4d1a",
                                marginTop: 0
                              }}
                            >
                              📝 Take Quiz
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p style={{ color: "#888" }}>Click to load lesson content</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && !generating && lessons.length === 0 && !error && (
          <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
            <p>No lessons available yet.</p>
            <button
              onClick={() => generateCurriculum(selectedSubject.id)}
              style={{ marginTop: "15px", background: "#1a4d1a" }}
            >
              Generate Curriculum
            </button>
          </div>
        )}
      </div>

      {/* Progress Summary */}
      {lessons.length > 0 && (
        <div className="card" style={{ marginTop: "15px" }}>
          <h4 style={{ color: "#FFD700", margin: "0 0 10px 0" }}>Progress</h4>
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <div style={{ flex: 1 }}>
              <div style={{ height: "8px", background: "#333", borderRadius: "4px" }}>
                <div style={{ 
                  width: `${(completedLessons.length / lessons.length) * 100}%`,
                  height: "100%",
                  background: completedLessons.length === lessons.length ? "#90EE90" : "#FFD700",
                  borderRadius: "4px",
                  transition: "width 0.3s"
                }} />
              </div>
            </div>
            <span style={{ color: "#fff", fontSize: "0.9rem" }}>
              {completedLessons.length}/{lessons.length} Complete
            </span>
          </div>
          {completedLessons.length === lessons.length && (
            <p style={{ color: "#90EE90", marginTop: "10px", fontSize: "0.9rem" }}>
              ✓ All lessons complete! You can now take the Chapter Exam.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default UnifiedCourses;
